/**
 * Classification table for NAD classification types with inline editing
 */

import { useState, useMemo, useCallback, useRef } from 'react'
import {
  Search,
  ChevronLeft,
  ChevronRight,
  Database,
  Check,
  X,
  Pencil,
  Loader2,
  Download,
  Upload,
  AlertCircle,
  CheckCircle,
} from 'lucide-react'
import {
  useClassificationList,
  useClassificationStats,
  useUpdateClassification,
  useImportClassificationCsv,
} from '../hooks/queries'
import { getClassificationExportUrl } from '../services/adminApi'
import type { ClassificationType, ClassificationListItem } from '../types'
import { CLASSIFICATION_LABELS } from '../types'
import { cn } from '../../lib/utils'

interface ClassificationTableProps {
  classificationType: ClassificationType
}

// Classification types that have bbsr_code field
const CLASSIFICATIONS_WITH_BBSR = new Set([
  'spatial-planning-regions',
  'labor-market-regions',
  'planning-regions',
  'travel-regions',
])

interface EditingState {
  rowId: number
  field: 'bbsr_code' | 'name' | 'description'
  value: string
}

interface ImportResult {
  status: 'success' | 'error'
  message: string
  updated?: number
  skipped?: number
}

function EditableCell({
  value,
  isEditing,
  editValue,
  onEdit,
  onSave,
  onCancel,
  onChange,
  isSaving,
  placeholder,
  className,
}: {
  value: string | null | undefined
  isEditing: boolean
  editValue: string
  onEdit: () => void
  onSave: () => void
  onCancel: () => void
  onChange: (value: string) => void
  isSaving: boolean
  placeholder?: string
  className?: string
}) {
  if (isEditing) {
    return (
      <div className="flex items-center gap-1">
        <input
          type="text"
          value={editValue}
          onChange={(e) => onChange(e.target.value)}
          className="flex-1 px-2 py-1 text-sm border border-blue-400 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
          autoFocus
          onKeyDown={(e) => {
            if (e.key === 'Enter') onSave()
            if (e.key === 'Escape') onCancel()
          }}
          disabled={isSaving}
        />
        <button
          onClick={onSave}
          disabled={isSaving}
          className="p-1 text-green-600 hover:bg-green-50 rounded"
          title="Speichern"
        >
          {isSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
        </button>
        <button
          onClick={onCancel}
          disabled={isSaving}
          className="p-1 text-red-600 hover:bg-red-50 rounded"
          title="Abbrechen"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
    )
  }

  return (
    <div
      className={cn(
        'group flex items-center gap-2 cursor-pointer hover:bg-gray-100 rounded px-1 -mx-1 min-h-[28px]',
        className
      )}
      onClick={onEdit}
      title="Klicken zum Bearbeiten"
    >
      <span className={cn(!value && 'text-gray-400 italic')}>
        {value || placeholder || '-'}
      </span>
      <Pencil className="w-3 h-3 text-gray-400 opacity-0 group-hover:opacity-100 transition-opacity" />
    </div>
  )
}

export function ClassificationTable({ classificationType }: ClassificationTableProps) {
  const [search, setSearch] = useState('')
  const [debouncedSearch, setDebouncedSearch] = useState('')
  const [page, setPage] = useState(0)
  const [editing, setEditing] = useState<EditingState | null>(null)
  const [importResult, setImportResult] = useState<ImportResult | null>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const limit = 50

  const hasBbsrCode = CLASSIFICATIONS_WITH_BBSR.has(classificationType)
  const isTextIndicator = classificationType === 'text-indicators'

  // Category labels for TextIndicators
  const categoryLabels: Record<string, string> = {
    '40': 'Kreise',
    '50': 'Gemeindeverbände',
    '60': 'Gemeinden',
  }

  // Debounce search
  useMemo(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search)
      setPage(0)
    }, 300)
    return () => clearTimeout(timer)
  }, [search])

  const { data, isLoading, error } = useClassificationList(classificationType, {
    limit,
    offset: page * limit,
    search: debouncedSearch,
  })

  const updateMutation = useUpdateClassification(classificationType)
  const importMutation = useImportClassificationCsv(classificationType)

  // Get data source info for classifications
  const { data: classificationStats } = useClassificationStats()

  const label = CLASSIFICATION_LABELS[classificationType]
  const totalPages = data ? Math.ceil(data.total / limit) : 0
  const dataSource = classificationStats?.data_source

  const startEditing = useCallback((row: ClassificationListItem, field: EditingState['field']) => {
    let currentValue = ''
    if (field === 'bbsr_code') currentValue = row.bbsr_code || ''
    else if (field === 'name') currentValue = row.name || ''
    else if (field === 'description') currentValue = row.description || ''

    setEditing({
      rowId: row.id,
      field,
      value: currentValue,
    })
  }, [])

  const cancelEditing = useCallback(() => {
    setEditing(null)
  }, [])

  const saveEditing = useCallback(async () => {
    if (!editing) return

    try {
      await updateMutation.mutateAsync({
        itemId: editing.rowId,
        data: {
          [editing.field]: editing.value || null,
        },
      })
      setEditing(null)
    } catch (error) {
      console.error('Failed to update:', error)
      // Keep editing state open on error
    }
  }, [editing, updateMutation])

  const updateEditValue = useCallback((value: string) => {
    setEditing((prev) => (prev ? { ...prev, value } : null))
  }, [])

  const handleExport = useCallback(() => {
    const url = getClassificationExportUrl(classificationType)
    window.open(url, '_blank')
  }, [classificationType])

  const handleImportClick = useCallback(() => {
    fileInputRef.current?.click()
  }, [])

  const handleFileSelect = useCallback(
    async (event: React.ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0]
      if (!file) return

      setImportResult(null)

      try {
        const result = await importMutation.mutateAsync(file)
        setImportResult({
          status: 'success',
          message: `${result.updated} Einträge aktualisiert, ${result.skipped} übersprungen`,
          updated: result.updated,
          skipped: result.skipped,
        })
      } catch (error) {
        setImportResult({
          status: 'error',
          message: error instanceof Error ? error.message : 'Import fehlgeschlagen',
        })
      }

      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = ''
      }
    },
    [importMutation]
  )

  // Auto-hide import result after 5 seconds
  useMemo(() => {
    if (importResult) {
      const timer = setTimeout(() => setImportResult(null), 5000)
      return () => clearTimeout(timer)
    }
  }, [importResult])

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">{label}</h1>
          <div className="flex items-center gap-2 mt-1">
            {data && (
              <span className="text-gray-500">
                {data.total.toLocaleString('de-DE')} Einträge
              </span>
            )}
            {dataSource && (
              <span className="text-sm text-blue-600 bg-blue-50 px-2 py-0.5 rounded">
                Stand: {dataSource.reference_date_display}
              </span>
            )}
          </div>
        </div>

        {/* Actions */}
        <div className="flex items-center gap-2">
          <button
            onClick={handleExport}
            className="flex items-center gap-2 px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
            title="Als CSV exportieren"
          >
            <Download className="w-4 h-4" />
            Export
          </button>
          <button
            onClick={handleImportClick}
            disabled={importMutation.isPending}
            className="flex items-center gap-2 px-3 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
            title="CSV importieren"
          >
            {importMutation.isPending ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <Upload className="w-4 h-4" />
            )}
            Import
          </button>
          <input
            ref={fileInputRef}
            type="file"
            accept=".csv"
            onChange={handleFileSelect}
            className="hidden"
          />
        </div>
      </div>

      {/* Data Source Info */}
      {dataSource && (
        <div className="flex items-center gap-2 text-sm text-gray-500">
          <Database className="w-4 h-4" />
          <span>GV100NAD Edition {dataSource.edition}</span>
        </div>
      )}

      {/* Import result notification */}
      {importResult && (
        <div
          className={cn(
            'flex items-center gap-2 p-3 rounded-lg border',
            importResult.status === 'success'
              ? 'bg-green-50 border-green-200 text-green-800'
              : 'bg-red-50 border-red-200 text-red-800'
          )}
        >
          {importResult.status === 'success' ? (
            <CheckCircle className="w-5 h-5" />
          ) : (
            <AlertCircle className="w-5 h-5" />
          )}
          <span>{importResult.message}</span>
        </div>
      )}

      {/* Info banner */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-sm text-blue-800">
        <strong>Tipp:</strong> Klicken Sie auf eine Zelle in den Spalten{' '}
        {hasBbsrCode && 'BBSR, '}Name oder Beschreibung, um den Wert zu bearbeiten.
        Alternativ können Sie die Daten als CSV exportieren, in Excel bearbeiten und wieder
        importieren.
      </div>

      {/* Search */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
        <input
          type="text"
          placeholder="Suchen..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
        />
      </div>

      {/* Table */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        {isLoading ? (
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900" />
          </div>
        ) : error ? (
          <div className="p-4 text-red-600">Fehler beim Laden der Daten</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="bg-gray-50 border-b border-gray-200">
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-24">
                    Code
                  </th>
                  {isTextIndicator && (
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-36">
                      Kategorie
                    </th>
                  )}
                  {hasBbsrCode && (
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-28">
                      BBSR
                    </th>
                  )}
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-64">
                    Name
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Beschreibung
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {data?.results.map((row) => {
                  const isEditingRow = editing?.rowId === row.id

                  return (
                    <tr key={row.id} className="hover:bg-gray-50">
                      {/* Code - read only */}
                      <td className="px-4 py-3 text-sm text-gray-900 whitespace-nowrap font-mono">
                        {row.code || '-'}
                      </td>

                      {/* Category - for TextIndicators only */}
                      {isTextIndicator && (
                        <td className="px-4 py-3 text-sm text-gray-500 whitespace-nowrap">
                          {row.category ? categoryLabels[row.category] || row.category : '-'}
                        </td>
                      )}

                      {/* BBSR - editable for region types */}
                      {hasBbsrCode && (
                        <td className="px-4 py-3 text-sm text-gray-500 whitespace-nowrap font-mono">
                          <EditableCell
                            value={row.bbsr_code}
                            isEditing={isEditingRow && editing?.field === 'bbsr_code'}
                            editValue={editing?.field === 'bbsr_code' ? editing.value : ''}
                            onEdit={() => startEditing(row, 'bbsr_code')}
                            onSave={saveEditing}
                            onCancel={cancelEditing}
                            onChange={updateEditValue}
                            isSaving={updateMutation.isPending}
                          />
                        </td>
                      )}

                      {/* Name - editable */}
                      <td className="px-4 py-3 text-sm text-gray-900">
                        <EditableCell
                          value={row.name}
                          isEditing={isEditingRow && editing?.field === 'name'}
                          editValue={editing?.field === 'name' ? editing.value : ''}
                          onEdit={() => startEditing(row, 'name')}
                          onSave={saveEditing}
                          onCancel={cancelEditing}
                          onChange={updateEditValue}
                          isSaving={updateMutation.isPending}
                          placeholder="nicht verfügbar"
                        />
                      </td>

                      {/* Description - editable */}
                      <td className="px-4 py-3 text-sm text-gray-500">
                        <EditableCell
                          value={row.description}
                          isEditing={isEditingRow && editing?.field === 'description'}
                          editValue={editing?.field === 'description' ? editing.value : ''}
                          onEdit={() => startEditing(row, 'description')}
                          onSave={saveEditing}
                          onCancel={cancelEditing}
                          onChange={updateEditValue}
                          isSaving={updateMutation.isPending}
                        />
                      </td>
                    </tr>
                  )
                })}
                {data?.results.length === 0 && (
                  <tr>
                    <td
                      colSpan={4 + (hasBbsrCode ? 1 : 0) + (isTextIndicator ? 1 : 0)}
                      className="px-4 py-8 text-center text-gray-500"
                    >
                      Keine Einträge gefunden
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Pagination */}
      {data && totalPages > 1 && (
        <div className="flex items-center justify-between">
          <p className="text-sm text-gray-500">
            Seite {page + 1} von {totalPages}
          </p>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setPage((p) => Math.max(0, p - 1))}
              disabled={page === 0}
              className={cn(
                'p-2 rounded-lg border transition-colors',
                page === 0
                  ? 'border-gray-200 text-gray-300 cursor-not-allowed'
                  : 'border-gray-300 text-gray-600 hover:bg-gray-50'
              )}
            >
              <ChevronLeft className="w-5 h-5" />
            </button>
            <button
              onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
              disabled={page >= totalPages - 1}
              className={cn(
                'p-2 rounded-lg border transition-colors',
                page >= totalPages - 1
                  ? 'border-gray-200 text-gray-300 cursor-not-allowed'
                  : 'border-gray-300 text-gray-600 hover:bg-gray-50'
              )}
            >
              <ChevronRight className="w-5 h-5" />
            </button>
          </div>
        </div>
      )}

      {/* Update error message */}
      {updateMutation.isError && (
        <div className="fixed bottom-4 right-4 bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded shadow-lg">
          Fehler beim Speichern: {updateMutation.error?.message}
        </div>
      )}
    </div>
  )
}
